import { useMemo, type ReactNode } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, Bar, BarChart, XAxis, YAxis, LabelList } from "recharts";
import Icon from "@/components/Icon";
import ChartTooltip from "@/components/Chart/ChartTooltip";
import { extendedColors, sysColors } from "@/assets/theme/colors";
import type { MaterialNames } from "@/components/material_symbol_names";
import type { RosterActivityCohort, RosterActivityEvent } from "@/features/roster-activity";
import { summarizePeriod } from "../summarizePeriod";

type Props = {
  events: RosterActivityEvent[];
  loading?: boolean;
};

/** Matches EventGroups Tag types: Member=highlight, Associate=surface, Student=outline (strong). */
const COHORT_CHART_COLORS: Record<RosterActivityCohort, string> = {
  MEMBER: extendedColors["extended-highlight-brand-color"],
  ASSOCIATE: sysColors["sys-brand-inverse-surface"],
  STUDENT: sysColors["sys-brand-surface-variant"]
};

const RADIAN = Math.PI / 180;

type CohortLabelProps = {
  cx?: number;
  cy?: number;
  midAngle?: number;
  outerRadius?: number;
  value?: number;
  percent?: number;
};

export function createCohortSliceLabel(sliceCount: number) {
  return function cohortSliceLabel({
    cx = 0,
    cy = 0,
    midAngle = 0,
    outerRadius = 0,
    value = 0,
    percent = 0
  }: CohortLabelProps) {
    if (!value) return null;

    // Thin rings clip multi-digit counts; one slice → center, several → just outside the arc.
    if (sliceCount === 1) {
      return (
        <text
          x={cx}
          y={cy}
          fill={sysColors["sys-brand-on-surface"]}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={16}
          fontWeight={600}
        >
          {value}
        </text>
      );
    }

    // Skip labels on tiny slices — tooltip still shows the count.
    if (percent < 0.08) return null;

    const radius = outerRadius + 12;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={sysColors["sys-brand-on-surface"]}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={11}
        fontWeight={600}
      >
        {value}
      </text>
    );
  };
}

const KIND_AXIS_WIDTH = 120;

type KindTickProps = {
  x?: string | number;
  y?: string | number;
  payload?: { value?: string };
};

function kindAxisTick({ x = 0, y = 0, payload }: KindTickProps) {
  return (
    <text
      x={Number(x)}
      y={Number(y)}
      dy={3}
      textAnchor="end"
      fill={sysColors["sys-brand-on-surface-variant"]}
      fontSize={10}
    >
      {payload?.value ?? ""}
    </text>
  );
}

type CompactMetric = {
  label: string;
  value: string;
  iconName: MaterialNames;
  testId: string;
};

function CompactMetricCard({ label, value, iconName, testId, loading }: CompactMetric & { loading?: boolean }) {
  return (
    <div
      className="flex flex-col gap-1 border border-sys-brand-outline rounded-lg px-2.5 py-2 min-w-0"
      data-testid={testId}
      aria-busy={loading || undefined}
      aria-label={loading ? label : undefined}
    >
      <div className="flex items-center gap-2 min-w-0">
        {loading ? (
          <div className="w-7 h-7 rounded-[6px] animate-pulse bg-gradient-to-r from-gray-200 to-gray-50 shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-[6px] items-center justify-center flex shrink-0 bg-extended-pgasilver-brand-color">
            <span aria-hidden="true">
              <Icon name={iconName} size={16} className="text-extended-pgasilver-brand-on-color" />
            </span>
          </div>
        )}
        {loading ? (
          <div className="animate-pulse h-5 rounded-full bg-gradient-to-r from-gray-200 to-gray-50 w-8" />
        ) : (
          <div className="text-title-medium text-sys-brand-on-surface leading-none">{value}</div>
        )}
      </div>
      {loading ? (
        <div className="animate-pulse h-3 rounded-full bg-gradient-to-r from-gray-200 to-gray-50 w-14" />
      ) : (
        <div className="text-label-small text-sys-brand-on-surface-variant leading-tight">{label}</div>
      )}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 border border-sys-brand-outline rounded-lg p-2.5 h-full min-w-0">
      <p className="text-label-medium text-sys-brand-on-surface m-0">{title}</p>
      <div className="w-full min-w-0">{children}</div>
    </div>
  );
}

function EmptyChartMessage({ label }: { label: string }) {
  return (
    <div className="flex h-[100px] items-center justify-center">
      <p className="text-body-small text-sys-brand-on-surface-variant m-0 text-center">{label}</p>
    </div>
  );
}

export default function PeriodStats({ events, loading = false }: Props) {
  const summary = useMemo(() => summarizePeriod(events), [events]);

  const metrics: CompactMetric[] = [
    {
      label: "Changes",
      value: String(summary.byGroup.changes),
      iconName: "sync_alt",
      testId: "roster-stat-changes"
    },
    {
      label: "Retirements",
      value: String(summary.byGroup.retirements),
      iconName: "beach_access",
      testId: "roster-stat-retirements"
    },
    {
      label: "In memoriam",
      value: String(summary.byGroup.inMemoriam),
      iconName: "favorite",
      testId: "roster-stat-in-memoriam"
    }
  ];

  const kindBarHeight = Math.max(100, summary.byKind.length * 22);
  const cohortSummaryText =
    summary.byCohort.length === 0
      ? "No roster activity in this period."
      : summary.byCohort.map((entry) => `${entry.name}: ${entry.value}`).join(", ");
  const kindSummaryText =
    summary.byKind.length === 0
      ? "No roster activity in this period."
      : summary.byKind.map((entry) => `${entry.name}: ${entry.value}`).join(", ");

  return (
    <section className="flex flex-col gap-2" data-testid="roster-activity-period-stats" aria-label="Period summary">
      <div className="grid gap-2 grid-cols-3">
        {metrics.map((metric) => (
          <CompactMetricCard key={metric.testId} {...metric} loading={loading} />
        ))}
      </div>

      {!loading ? (
        <div className="grid gap-2 grid-cols-2 items-stretch">
          <ChartCard title="By member type">
            <p className="sr-only">{cohortSummaryText}</p>
            {summary.byCohort.length === 0 ? (
              <EmptyChartMessage label="No Roster Activity in This Period" />
            ) : (
              <ResponsiveContainer width="100%" height={120}>
                <PieChart margin={{ top: 4, right: 12, bottom: 4, left: 12 }}>
                  <Pie
                    data={summary.byCohort}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={28}
                    outerRadius={40}
                    paddingAngle={2}
                    label={createCohortSliceLabel(summary.byCohort.length)}
                    labelLine={false}
                  >
                    {summary.byCohort.map((entry) => (
                      <Cell key={entry.cohort} fill={COHORT_CHART_COLORS[entry.cohort]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active = false, payload }) => {
                      const items =
                        payload?.map((entry) => ({
                          name: String(entry.name ?? ""),
                          value: Number(entry.value ?? 0),
                          color: String(entry.payload?.fill ?? entry.color ?? "")
                        })) ?? [];
                      return <ChartTooltip active={active} title="Member type" items={items} />;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="By change type">
            <p className="sr-only">{kindSummaryText}</p>
            {summary.byKind.length === 0 ? (
              <EmptyChartMessage label="No Roster Activity in This Period" />
            ) : (
              <ResponsiveContainer width="100%" height={kindBarHeight}>
                <BarChart data={summary.byKind} layout="vertical" margin={{ top: 0, right: 28, left: 0, bottom: 0 }}>
                  <XAxis type="number" allowDecimals={false} hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={KIND_AXIS_WIDTH}
                    interval={0}
                    tick={kindAxisTick}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "transparent" }}
                    content={({ active = false, payload }) => {
                      const items =
                        payload?.map((entry) => ({
                          name: String(entry.payload?.name ?? entry.name ?? ""),
                          value: Number(entry.value ?? 0),
                          color: String(entry.color ?? sysColors["sys-brand-primary"])
                        })) ?? [];
                      return <ChartTooltip active={active} title="Change type" items={items} />;
                    }}
                  />
                  <Bar dataKey="value" fill={sysColors["sys-brand-primary"]} radius={[0, 4, 4, 0]} barSize={10}>
                    <LabelList
                      dataKey="value"
                      position="right"
                      offset={6}
                      style={{
                        fill: sysColors["sys-brand-on-surface"],
                        fontSize: 11,
                        fontWeight: 600
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      ) : null}
    </section>
  );
}
